import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, InputAdornment, Paper, Snackbar, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  Tabs, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLongOutlined";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import OrdenFormDialog from "../components/OrdenFormDialog";
import ReporteOrdenesCompra from "../components/ReporteOrdenesCompra";
import { fmt, getCatalogosOC, listarOrdenes, mensajeError, simbolo } from "../services/ordenCompraService";

const headSx = { fontWeight: 800, whiteSpace: "nowrap", fontSize: 12, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 };
const cellSx = { py: 1, px: 1.5, fontSize: 13, verticalAlign: "middle" };
const FILTROS = [{ clave: "", texto: "Todas", color: "primary" }, { clave: "Proceso", texto: "En proceso", color: "success" }, { clave: "Finalizado", texto: "Finalizadas", color: "default" }];

// Órdenes de compra de equipos y materiales: lista con búsqueda y paginación; el detalle (líneas, aduana, pagos) está en /ordenes-compra/:id.
export default function OrdenesCompraPage() {
  const navigate = useNavigate();
  const [catalogos, setCatalogos] = useState(null);
  const [filas, setFilas] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [buscar, setBuscar] = useState("");
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("");
  const [pagina, setPagina] = useState(0);
  const [limite, setLimite] = useState(10);
  const [nueva, setNueva] = useState(false);
  const [tab, setTab] = useState(0); // 0 órdenes, 1 reporte

  useEffect(() => {
    getCatalogosOC().then(({ data }) => setCatalogos(data)).catch((e) => setError(mensajeError(e, "No se pudieron cargar los datos del formulario.")));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setQ(buscar.trim()); setPagina(0); }, 300);
    return () => clearTimeout(t);
  }, [buscar]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await listarOrdenes({ q: q || undefined, estado: estado || undefined, page: pagina + 1, limit: limite });
      setFilas(data.rows);
      setTotal(data.total);
      setError("");
    } catch (e) {
      setError(mensajeError(e, "No se pudieron cargar las órdenes de compra."));
    } finally {
      setCargando(false);
    }
  }, [q, estado, pagina, limite]);
  useEffect(() => { cargar(); }, [cargar]);

  const abrir = (id) => navigate(`/ordenes-compra/${id}`);
  const alGuardar = ({ id, aviso: problema }) => {
    setNueva(false);
    if (problema) setAviso(problema);
    abrir(id);
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Órdenes de compra"
        subtitle="Compras de equipos y materiales: proveedor, detalle, aduana y pagos"
        showBack={false}
        actions={tab === 0 && <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setNueva(true)} disabled={!catalogos} sx={{ textTransform: "none", fontWeight: 700 }}>Registrar orden de compra</Button>}
      />

      <Box sx={{ p: { xs: 1.5, md: 2 }, display: "grid", gap: 1.5 }}>
        <Paper variant="outlined">
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 38, "& .MuiTab-root": { minHeight: 38, textTransform: "none", fontWeight: 700, fontSize: 13 } }}>
            <Tab label="Órdenes de compra" />
            <Tab label="Reporte" />
          </Tabs>
        </Paper>
        {tab === 1 && <ReporteOrdenesCompra />}
        {tab === 0 && <>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }} role="group" aria-label="Filtrar por estado">
            {FILTROS.map((f) => {
              const activo = estado === f.clave;
              return <Chip key={f.clave || "todas"} label={f.texto} onClick={() => { setEstado(f.clave); setPagina(0); }} color={activo ? f.color : "default"} variant={activo ? "filled" : "outlined"} sx={{ fontWeight: 700, fontSize: 12.5 }} aria-pressed={activo} />;
            })}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: { xs: "100%", sm: "auto" } }}>
            {cargando && <CircularProgress size={18} />}
            <TextField
              size="small" placeholder="Buscar orden, proveedor o cliente…" value={buscar} onChange={(e) => setBuscar(e.target.value)} sx={{ width: { xs: "100%", sm: 380 } }}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
            />
          </Box>
        </Box>

        {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}

        <Paper variant="outlined">
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={headSx}>N.º</TableCell>
                  <TableCell sx={headSx}>Fecha</TableCell>
                  <TableCell sx={headSx}>Proveedor / Cliente</TableCell>
                  <TableCell sx={headSx}>Embarcador</TableCell>
                  <TableCell sx={headSx} align="center">Cuotas</TableCell>
                  <TableCell sx={headSx} align="right">Monto</TableCell>
                  <TableCell sx={headSx}>Observación</TableCell>
                  <TableCell sx={headSx}>Usuario</TableCell>
                  <TableCell sx={headSx}>Estado</TableCell>
                  <TableCell sx={headSx} align="center">Ver</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!filas.length ? (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ py: 5, textAlign: "center" }}>
                      {cargando ? <Typography variant="body2" color="text.secondary">Cargando…</Typography> : (
                        <Box sx={{ display: "grid", justifyItems: "center", gap: 1 }}>
                          <ReceiptLongIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                          <Typography sx={{ fontWeight: 700 }}>{q || estado ? "No hay órdenes con ese filtro" : "Todavía no hay órdenes de compra"}</Typography>
                          {(q || estado) && <Button size="small" onClick={() => { setBuscar(""); setQ(""); setEstado(""); }} sx={{ textTransform: "none" }}>Quitar filtros</Button>}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ) : filas.map((o) => (
                  <TableRow key={o.id} hover onClick={() => abrir(o.id)} sx={{ cursor: "pointer" }}>
                    <TableCell sx={{ ...cellSx, fontWeight: 800 }}>{o.id}</TableCell>
                    <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>{o.fecha}</TableCell>
                    <TableCell sx={{ ...cellSx, minWidth: 200 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{o.proveedor}</Typography>
                      {o.cliente && <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Para: {o.cliente}</Typography>}
                    </TableCell>
                    <TableCell sx={cellSx}>{o.embarcado}</TableCell>
                    <TableCell sx={{ ...cellSx, color: "text.primary" }} align="center">{o.cantidad_cuotas}</TableCell>
                    <TableCell sx={{ ...cellSx, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }} align="right">
                      {o.lineas ? (
                        <>
                          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{simbolo(o.moneda)} {fmt(o.monto_total)}</Typography>
                          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{o.lineas} línea{o.lineas === 1 ? "" : "s"}</Typography>
                        </>
                      ) : <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Sin detalle</Typography>}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, maxWidth: 260 }}><Typography sx={{ fontSize: 12.5 }} noWrap title={o.observacion}>{o.observacion}</Typography></TableCell>
                    <TableCell sx={cellSx}>{o.usu}</TableCell>
                    <TableCell sx={cellSx}><Chip size="small" label={o.estado === "Proceso" ? "En proceso" : "Finalizada"} color={o.estado === "Proceso" ? "success" : "default"} sx={{ fontWeight: 700 }} /></TableCell>
                    <TableCell sx={cellSx} align="center">
                      <Tooltip title="Ver orden"><IconButton size="small" aria-label={`Ver orden ${o.id}`} onClick={(e) => { e.stopPropagation(); abrir(o.id); }}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div" count={total} page={pagina} rowsPerPage={limite} rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_, p) => setPagina(p)} onRowsPerPageChange={(e) => { setLimite(Number(e.target.value)); setPagina(0); }}
            labelRowsPerPage="Filas" labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </Paper>
        </>}
      </Box>

      <OrdenFormDialog open={nueva} orden={null} catalogos={catalogos} onClose={() => setNueva(false)} onGuardada={alGuardar} />
      <Snackbar open={!!aviso} autoHideDuration={6000} onClose={() => setAviso("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="warning" variant="filled" onClose={() => setAviso("")}>{aviso}</Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
